import { useState } from "react";

import { useMutation } from "@apollo/client";
import { HiOutlineArrowPathRoundedSquare } from "react-icons/hi2";
import { LuWorkflow } from "react-icons/lu";
import { MdPause, MdPlayArrow } from "react-icons/md";
import { toast } from "sonner";

import { TimeIcon } from "@chakra-ui/icons";
import { useDisclosure } from "@chakra-ui/react";

import { FragmentType, getFragmentData, graphql } from "@/__generated__";
import { EnvironmentStateEnum } from "@/__generated__/graphql";
import { ActionFunctionType, ActionItem, Actions } from "@/components/Actions";
import DeleteEnvironment from "@/components/DeleteModals/DeleteEnvironment";
import { DeleteIcon, DeployRocketIcon } from "@/components/shared/ReleaseIcons";
import {
  useEnvironmentPatchMutation,
  useEnvironmentSetupMutation,
  useEnvironmentCustomWorkflowMutation,
} from "@/hooks/useEnvironmentPipelineMutation";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import { assertHasNoUserErrors } from "@/utils/legacyErrorHandler";
import { operationErrorHandler } from "@/utils/operationErrorHandler";

const MEnvironmentActions_Extend = graphql(`
  mutation MEnvironmentActions_Extend($input: EnvironmentsExtendInput!) {
    environmentsExtend(input: $input) {
      environment {
        ...EnvironmentActions_Environment
      }
      userErrors {
        ... on OperationError {
          message
        }
      }
    }
  }
`);

const MEnvironmentActions_Pause = graphql(`
  mutation MEnvironmentActions_Pause($input: EnvironmentsPauseInput!) {
    environmentsPause(input: $input) {
      environment {
        ...EnvironmentActions_Environment
      }
      userErrors {
        ... on OperationError {
          message
        }
      }
    }
  }
`);

const MEnvironmentActions_Resume = graphql(`
  mutation MEnvironmentActions_Resume($input: EnvironmentsResumeInput!) {
    environmentsResume(input: $input) {
      environment {
        ...EnvironmentActions_Environment
      }
      userErrors {
        ... on OperationError {
          message
        }
      }
    }
  }
`);

const EnvironmentActions_Environment = graphql(`
  fragment EnvironmentActions_Environment on Environment {
    id
    expiresAt
    state
    parentEnvironment {
      id
      state
    }
    latestConfiguration {
      id
      workflows {
        name
      }
    }
    ...DeleteEnvironment_Environment
  }
`);

type Props = {
  environment?: FragmentType<typeof EnvironmentActions_Environment>;
};

export function EnvironmentActions({ environment: propsEnvironment }: Props) {
  const environment = getFragmentData(EnvironmentActions_Environment, propsEnvironment);
  const disclosureProps = useDisclosure();
  const { workspaceId, applicationId, environmentId } = useReadySearchParams();
  const [customWorkflowLoading, setCustomWorkflowLoading] = useState(false);

  const [environmentSetup, { loading: setupLoading }] = useEnvironmentSetupMutation({
    workspaceId,
    applicationId,
    environmentId,
  });
  const [environmentPatch, { loading: patchLoading }] = useEnvironmentPatchMutation({
    workspaceId,
    applicationId,
    environmentId,
  });
  const environmentCustomWorkflow = useEnvironmentCustomWorkflowMutation({
    workspaceId,
    applicationId,
    environmentId,
  });

  const removed = environment?.state && [EnvironmentStateEnum.Removed].includes(environment.state);

  const canResume =
    environment?.state &&
    [EnvironmentStateEnum.Paused, EnvironmentStateEnum.ResumingErrored].includes(environment.state);

  const canPause =
    environment?.state &&
    ![EnvironmentStateEnum.Paused, EnvironmentStateEnum.Pausing].includes(environment.state) &&
    !canResume;

  const [pause, { loading: pauseLoading }] = useMutation(MEnvironmentActions_Pause);
  const [resume, { loading: resumeLoading }] = useMutation(MEnvironmentActions_Resume);
  const [extendEnvironment, { loading: extendLoading }] = useMutation(MEnvironmentActions_Extend);

  const onPause = async () => {
    try {
      const { data } = await pause({
        variables: { input: { id: environmentId } },
      });

      assertHasNoUserErrors(data?.environmentsPause?.userErrors);

      toast.success("Environment paused");
    } catch (error) {
      operationErrorHandler(error);
    }
  };

  const onResume = async () => {
    try {
      const { data } = await resume({
        variables: { input: { id: environmentId } },
      });

      assertHasNoUserErrors(data?.environmentsResume?.userErrors);

      toast.success("Environment resumed");
    } catch (error) {
      operationErrorHandler(error);
    }
  };

  const onPauseResume = async () => {
    if (canResume) {
      onResume();
      return;
    }

    if (canPause) {
      onPause();
      return;
    }

    toast.error("Unable to pause or resume");
  };

  const onExtend = async () => {
    try {
      const { data } = await extendEnvironment({ variables: { input: { id: environmentId } } });

      assertHasNoUserErrors(data?.environmentsExtend?.userErrors);

      toast.success("Environment expiration extended");
    } catch (error) {
      operationErrorHandler(error);
    }
  };

  const pauseResumeActionIcon = () => {
    if (canResume) {
      return MdPlayArrow;
    } else {
      return MdPause;
    }
  };

  const customWorkflows: ActionFunctionType[] =
    environment?.latestConfiguration?.workflows.reduce((array, workflow) => {
      if (!["setup", "patch", "teardown"].includes(workflow.name)) {
        array.push({
          type: "function",
          label: `Deploy ${workflow.name}`,
          icon: LuWorkflow,
          function: () => {
            setCustomWorkflowLoading(true);
            environmentCustomWorkflow(workflow.name)
              .then(() => setCustomWorkflowLoading(false))
              .catch(() => setCustomWorkflowLoading(false));
          },
        });
      }
      return array;
    }, [] as ActionFunctionType[]) || [];

  const actionItems: ActionFunctionType[] = [
    {
      type: "function",
      label: "Deploy Setup",
      icon: DeployRocketIcon,
      function: () => environmentSetup(),
    },
    {
      type: "function",
      label: "Deploy Patch",
      icon: HiOutlineArrowPathRoundedSquare,
      function: () => environmentPatch(),
    },
    ...customWorkflows,
  ];

  const actions: ActionItem[] = [
    {
      type: "menu",
      label: "Deploy",
      items: actionItems,
      isLoading: setupLoading || patchLoading || customWorkflowLoading,
    },
  ];

  // If the Environment is from App Imports, don't allow any of these actions to happen
  const addActionMenu = environment ? !environment.parentEnvironment : false;
  const actionMenu: ActionItem = {
    type: "menu",
    label: "Actions",
    items: [
      {
        type: "function",
        label: "Extend",
        icon: TimeIcon,
        function: onExtend,
      },
      {
        type: "function",
        label: canResume ? "Resume" : "Pause",
        icon: pauseResumeActionIcon(),
        function: onPauseResume,
        isDisabled: !(canPause || canResume),
      },
      {
        type: "function",
        label: "Delete",
        icon: DeleteIcon,
        isDisabled: removed,
        function: disclosureProps.onOpen,
      },
    ],
    isLoading: pauseLoading || resumeLoading || extendLoading,
  };

  if (addActionMenu) {
    actions.push(actionMenu);
  }

  if (!environment) return null;

  return (
    <>
      <Actions actions={actions} />
      <DeleteEnvironment environment={environment} {...disclosureProps} />
    </>
  );
}
